import { isTotalCostOfOwnershipEnabled } from '@/featureFlags';
import { t, Trans } from '@lingui/macro';
import { formatRelativeTime, getIntercomArticleURL, isPresent, transEnum } from '@luminovo/commons';
import {
    FieldCheckboxControlled,
    FieldDateControlled,
    FieldMultiSelectControlled,
    FieldRadioControlled,
    FieldSelectControlled,
    Flexbox,
    FormItem,
    Link,
    Text,
} from '@luminovo/design-system';
import { Packaging, SolutionComparisonCriteria, SourcingPreferenceOptions } from '@luminovo/http-client';
import { packagingTranslations, sourcingPreferenceTranslations } from '@luminovo/sourcing-core';
import { InputAdornment } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { SourcingScenarioFormValues } from './types';

const FilterPreferenceSection = () => {
    const { control, setValue } = useFormContext<SourcingScenarioFormValues>();
    const leadTimePrefType = useWatch({ control, name: 'solutionPreferences.leadTimePreference.type' });
    const leadTimeDate = useWatch({ control, name: 'solutionPreferences.leadTimePreference.target' });

    return (
        <Flexbox flexDirection="column" gap={16}>
            <FormItem label={t`Lead time preference`}>
                <FieldSelectControlled
                    control={control}
                    name="solutionPreferences.leadTimePreference.type"
                    FieldProps={{
                        style: { width: '40%' },
                        disableClearable: true,
                        options: Object.values(SourcingPreferenceOptions),
                        getOptionLabel: (option) => transEnum(option, sourcingPreferenceTranslations),
                        onValueChange: () => setValue('solutionPreferences.leadTimePreference.target', undefined),
                    }}
                />
                {leadTimePrefType === SourcingPreferenceOptions.BestPriceBy && (
                    <FieldDateControlled
                        control={control}
                        name="solutionPreferences.leadTimePreference.target"
                        required={true}
                        inFuture={true}
                        FieldProps={{
                            style: { width: '40%' },
                            InputProps: {
                                endAdornment: isPresent(leadTimeDate) && (
                                    <InputAdornment position="end">{formatRelativeTime(leadTimeDate)}</InputAdornment>
                                ),
                            },
                        }}
                    />
                )}
                <FormItem label={t`Which price should be optimized?`}>
                    <Flexbox flexDirection="column" gap={8}>
                        <Flexbox alignItems="center" gap={8}>
                            <FieldRadioControlled
                                control={control}
                                name="solutionPreferences.solutionComparisonCriteria"
                                FieldProps={{ fieldValue: SolutionComparisonCriteria.UnitPrice }}
                            />
                            <Text>
                                <Trans>Unit price</Trans>
                            </Text>
                        </Flexbox>
                        <Flexbox alignItems="center" gap={8}>
                            <FieldRadioControlled
                                control={control}
                                name="solutionPreferences.solutionComparisonCriteria"
                                FieldProps={{ fieldValue: SolutionComparisonCriteria.TotalPrice }}
                            />
                            <Text>
                                <Trans>Total price</Trans>
                            </Text>
                        </Flexbox>
                    </Flexbox>
                </FormItem>
            </FormItem>

            <FormItem
                label={t`Exclude offers with`}
                variant="description-inlined"
                description={
                    <Trans>
                        Please see our{' '}
                        <Link
                            variant="body-small"
                            href={getIntercomArticleURL(84955)}
                            rel="noopener noreferrer"
                            target="_blank"
                        >
                            help page article
                        </Link>{' '}
                        for the price types definition.
                    </Trans>
                }
            >
                <Flexbox alignItems="center" gap={8}>
                    <FieldCheckboxControlled
                        name="solutionPreferences.excludedPriceTypes.listPrice"
                        control={control}
                    />
                    <Text>
                        <Trans>List prices</Trans>
                    </Text>
                </Flexbox>
                <Flexbox alignItems="center" gap={8}>
                    <FieldCheckboxControlled
                        name="solutionPreferences.excludedPriceTypes.contractPrice"
                        control={control}
                    />
                    <Text>
                        <Trans>Contract prices</Trans>
                    </Text>
                </Flexbox>
                <Flexbox alignItems="center" gap={8}>
                    <FieldCheckboxControlled
                        name="solutionPreferences.excludedPriceTypes.quotePrice"
                        control={control}
                    />
                    <Text>
                        <Trans id="excluded.quoteprices.indicator">Quote prices</Trans>
                    </Text>
                </Flexbox>
                <Flexbox alignItems="center" gap={8}>
                    <FieldCheckboxControlled
                        name="solutionPreferences.excludedPriceTypes.customerNegotiatedPrice"
                        control={control}
                    />
                    <Text>
                        <Trans>Customer negotiated prices</Trans>
                    </Text>
                </Flexbox>
                <Flexbox alignItems="center" gap={8}>
                    <FieldCheckboxControlled
                        name="solutionPreferences.excludedPriceTypes.purchasePrice"
                        control={control}
                    />
                    <Text>
                        <Trans>Purchase prices</Trans>
                    </Text>
                </Flexbox>
                <Flexbox alignItems="center" gap={8}>
                    <FieldCheckboxControlled
                        name="solutionPreferences.excludedPriceTypes.standardPrice"
                        control={control}
                    />
                    <Text>
                        <Trans>Standard prices</Trans>
                    </Text>
                </Flexbox>
            </FormItem>
        </Flexbox>
    );
};

const InventroyPreferenceSections = () => {
    const { control } = useFormContext<SourcingScenarioFormValues>();

    return (
        <Flexbox flexDirection="column" gap={8}>
            <FormItem
                label={t`Which offers should be prioritized?`}
                variant="description-inlined"
                description={t`Given similar offers from your own inventory and the external market, you can choose here which one has more weight. If Neutral is selected, both inventory and market offers will be treated equally. If Market offers is selected, inventory will only be chosen if all other offers are red.`}
                width={'800px'}
            >
                <Flexbox alignItems="center" gap={8}>
                    <FieldRadioControlled
                        name="solutionPreferences.inventoryPreference"
                        control={control}
                        FieldProps={{ fieldValue: 'PreferInventory' }}
                    />
                    <Text>
                        <Trans>Inventory</Trans>
                    </Text>
                </Flexbox>
                <Flexbox alignItems="center" gap={8}>
                    <FieldRadioControlled
                        name="solutionPreferences.inventoryPreference"
                        control={control}
                        FieldProps={{ fieldValue: 'Neutral' }}
                    />
                    <Text>
                        <Trans>No prioritization</Trans>
                    </Text>
                </Flexbox>
                <Flexbox alignItems="center" gap={8}>
                    <FieldRadioControlled
                        name="solutionPreferences.inventoryPreference"
                        control={control}
                        FieldProps={{ fieldValue: 'PreferMarketOffers' }}
                    />
                    <Text>
                        <Trans>Market offers</Trans>
                    </Text>
                </Flexbox>
            </FormItem>
        </Flexbox>
    );
};

const OtherPreferenceSections = () => {
    const { control } = useFormContext<SourcingScenarioFormValues>();

    return (
        <Flexbox id="packaging-preference" flexDirection="column" gap={20}>
            <FormItem
                label={t`Packaging preference`}
                variant="description-inlined"
                description={t`Select which packaging types you would like to prefer. If no specific packaging should be preferred, leave the selection empty.`}
            >
                <FieldMultiSelectControlled
                    control={control}
                    name="solutionPreferences.packagingPreference"
                    FieldProps={{
                        getOptionKey: (option) => option,
                        options: Object.values(Packaging),
                        getOptionLabel: (option) => transEnum(option, packagingTranslations),
                        style: { width: '400px' },
                        placeholder: t`Select packaging`,
                    }}
                />
            </FormItem>

            <FormItem label={t`Prefer supplier API integrations`}>
                <Flexbox alignItems="center" gap={8}>
                    <FieldCheckboxControlled name="solutionPreferences.preferDirectApiIntegrations" control={control} />
                    <Text>
                        <Trans>Prefer direct supplier API integrations over third-party sources (e.g. Octopart).</Trans>
                    </Text>
                </Flexbox>
            </FormItem>
            <FormItem
                label={t`Exclude custom part offers where total offered quantity does not match the scenarios quantity`}
            >
                <Flexbox alignItems="center" gap={8}>
                    <FieldCheckboxControlled
                        name="solutionPreferences.excludeMismatchedPricePoints"
                        control={control}
                    />
                    <Text>
                        <Trans>
                            Exclude custom part offers where total offered quantity does not match the scenarios
                            quantity
                        </Trans>
                    </Text>
                </Flexbox>
            </FormItem>
            <FormItem label={t`Prioritize preferred manufacturers`}>
                <Flexbox alignItems="center" gap={8}>
                    <FieldCheckboxControlled
                        name="solutionPreferences.prioritizePreferredManufacturers"
                        control={control}
                    />
                    <Text>
                        <Trans>Prioritize offers from preferred manufacturers</Trans>
                    </Text>
                </Flexbox>
            </FormItem>
            <FormItem label={t`Prioritize customer negotiated prices`}>
                <Flexbox alignItems="center" gap={8}>
                    <FieldCheckboxControlled
                        name="solutionPreferences.preferCustomerNegotiatedPrices"
                        control={control}
                    />
                    <Text>
                        <Trans>Prioritize offers with customer negotiated prices</Trans>
                    </Text>
                </Flexbox>
            </FormItem>
            {/* TODO: Remove this once the feature is fully released */}
            {isTotalCostOfOwnershipEnabled() && (
                <FormItem label={t`Enable total cost of ownership`}>
                    <Flexbox alignItems="center" gap={8}>
                        <FieldCheckboxControlled name="solutionPreferences.isTcoEnabled" control={control} />
                        <Text>
                            <Trans>Enable total cost of ownership rules.</Trans>
                        </Text>
                    </Flexbox>
                </FormItem>
            )}
        </Flexbox>
    );
};

export function SolutionPreferencesTab() {
    return (
        <Flexbox
            flexDirection="column"
            style={{ width: '100%', alignSelf: 'center', alignItems: 'center', overflow: 'auto' }}
        >
            <Flexbox flexDirection="column" gap={'28px'} width={'800px'} alignSelf="center">
                <FilterPreferenceSection />
                <InventroyPreferenceSections />
                <OtherPreferenceSections />
            </Flexbox>
        </Flexbox>
    );
}
